import { type ReactNode, useId } from 'react'
import { MonitorIcon, MoonIcon, SunIcon } from './theme-icons.tsx'
import { type ThemePreference, useThemePreference } from './theme-store.ts'

/**
 * The two ways to choose the console's colour scheme — both views of the one store in
 * `theme-store.ts`, so choosing in either is the other changing too.
 *
 * Each is a **real radio group**: a `<fieldset>` named by a `<legend>`, three
 * `<input type="radio">` and their labels, drawn as segments. The browser supplies the keyboard
 * model (Tab enters at the chosen option, the arrow keys move *and* choose, Space chooses) and
 * the screen reader supplies "2 of 3"; nothing here re-implements either. The inputs are hidden
 * visually and not from the keyboard — the same trick as the feature-flag switch — and the segment
 * beside each draws the focus ring and the chosen state.
 *
 * **Each group has its own `name`.** Radios with one name are one group to the browser, wherever
 * they sit, and the arrow keys would walk from the account page's control into the masthead's.
 */

type Option = {
  readonly value: ThemePreference
  readonly label: string
  readonly icon: ReactNode
}

const OPTIONS: readonly Option[] = [
  { value: 'system', label: 'System', icon: <MonitorIcon /> },
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
]

function Segments({
  name,
  label,
  showIcon,
  showText,
}: {
  readonly name: string
  /** How an option is named: its own word, or "<word> theme". */
  readonly label: (option: Option) => string
  readonly showIcon: boolean
  readonly showText: boolean
}) {
  const [preference, setPreference] = useThemePreference()

  return (
    <>
      {OPTIONS.map((option) => (
        // The label is the segment and the click target; the title is the masthead's tooltip.
        <label key={option.value} className="segment" title={showText ? undefined : label(option)}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={preference === option.value}
            onChange={() => {
              setPreference(option.value)
            }}
          />
          <span className="segment-face">
            {showIcon ? option.icon : null}
            <span className={showText ? 'segment-text' : 'visually-hidden'}>{label(option)}</span>
          </span>
        </label>
      ))}
    </>
  )
}

/**
 * The Theme card's control on Your account: three words, the chosen one marked by a heavier
 * label, a ring and a dot as well as by colour, and a one-line note that this is not an account
 * setting. 44 px tall below 40 rem.
 */
export function ThemeChoice() {
  const name = useId()

  return (
    <fieldset className="theme-choice">
      <legend className="theme-choice-legend">Colour scheme</legend>
      <div className="segmented">
        <Segments name={name} label={(option) => option.label} showIcon={false} showText />
      </div>
      <p className="small muted">This browser only.</p>
    </fieldset>
  )
}

/**
 * The masthead's compact switch: monitor, sun, moon. The words are there for assistive technology
 * ("System theme", "Light theme", "Dark theme") and for the hover tooltip, and not on screen.
 * Hidden below 40 rem, where Your account has the control — see `styles.css`.
 */
export function ThemeSwitch() {
  const name = useId()

  return (
    <fieldset className="theme-switch">
      <legend className="visually-hidden">Theme</legend>
      <div className="segmented">
        <Segments
          name={name}
          label={(option) => `${option.label} theme`}
          showIcon
          showText={false}
        />
      </div>
    </fieldset>
  )
}
